import * as React from 'react';

import {
  EAttribute_InputType,
  MemberCompanyDirectoryAttributeFilterInput,
  MemberCompanyDirectorySearchInput,
} from '@/graphql-types/globalTypes';
import { Filter, getFilterFacetValues } from '@/hooks/useFilterableAttributes';

import { MemberCompanyDirectoryFilterOptions } from '../types';

const buildAttributeSchemas = (
  filterOptions: MemberCompanyDirectoryFilterOptions | undefined
) => {
  return (
    filterOptions?.attributeFilters.map((schema) => ({
      _id: schema._id,
      name: schema.name,
      inputType: schema.inputType,
      options: schema.options.map((option) => ({
        id: option.label,
        label: option.label,
      })),
    })) ?? []
  );
};

const NO_ATTRIBUTE_FILTERS:
  | MemberCompanyDirectoryAttributeFilterInput[]
  | null = null;

export const isFreeTextCompanyDirectoryFilter = (filter: Filter): boolean =>
  filter.attributeSchema.inputType === EAttribute_InputType.FREE_TEXT;

export const buildMemberCompanyDirectorySearchFilters = (
  selectedFilters: Filter[],
  freeTextValues: Record<string, string>
): Pick<MemberCompanyDirectorySearchInput, 'attributeFilters'> => {
  const attributeFilters: MemberCompanyDirectoryAttributeFilterInput[] = [];

  selectedFilters.forEach((filter) => {
    const { attributeSchema, facet } = filter;
    if (isFreeTextCompanyDirectoryFilter({ attributeSchema, facet })) {
      const text = freeTextValues[attributeSchema._id]?.trim() ?? '';
      if (!text) {
        return;
      }

      attributeFilters.push({
        attributeSchemaId: attributeSchema._id,
        value: text,
      });
      return;
    }

    getFilterFacetValues(filter).forEach((value) => {
      attributeFilters.push({
        attributeSchemaId: attributeSchema._id,
        value,
      });
    });
  });

  return {
    attributeFilters:
      attributeFilters.length > 0 ? attributeFilters : NO_ATTRIBUTE_FILTERS,
  };
};

export const useMemberCompanyDirectoryFilters = (
  filterOptions: MemberCompanyDirectoryFilterOptions | undefined,
  isLoadingOptions: boolean
) => {
  const attributeSchemas = React.useMemo(
    () => buildAttributeSchemas(filterOptions),
    [filterOptions]
  );

  const [_selectedFilters, setSelectedFilters] = React.useState<Filter[]>([]);

  const selectFilter = React.useCallback(
    (params: {
      attributeSchemaId: string;
      facet?: string;
      facets?: string[];
    }): void => {
      const attributeSchema = attributeSchemas.find(
        (schema) => schema._id === params.attributeSchemaId
      );

      if (attributeSchema) {
        setSelectedFilters((previousValue) => [
          ...previousValue.filter(
            (filter) => filter.attributeSchema._id !== params.attributeSchemaId
          ),
          {
            attributeSchema,
            facet: params.facet,
            ...(params.facets ? { facets: params.facets } : {}),
          },
        ]);
      }
    },
    [attributeSchemas]
  );

  const unselectFilter = React.useCallback(
    (params: { attributeSchemaId: string }) => {
      setSelectedFilters((previousValue) =>
        previousValue.filter(
          (filter) => filter.attributeSchema._id !== params.attributeSchemaId
        )
      );
    },
    []
  );

  const unselectedFilters: Filter[] = React.useMemo(() => {
    return attributeSchemas
      .filter(
        (attributeSchema) =>
          !_selectedFilters
            .map((filter) => filter.attributeSchema._id)
            .includes(attributeSchema._id)
      )
      .map((attributeSchema) => ({ attributeSchema, facet: undefined }));
  }, [attributeSchemas, _selectedFilters]);

  const selectedFilters: Filter[] = React.useMemo(() => {
    return [..._selectedFilters].sort((a, b) => {
      if (a.attributeSchema.name < b.attributeSchema.name) {
        return -1;
      }
      if (a.attributeSchema.name > b.attributeSchema.name) {
        return 1;
      }
      return 0;
    });
  }, [_selectedFilters]);

  const someSelectFilterApplied = selectedFilters.some(
    (filter) =>
      !isFreeTextCompanyDirectoryFilter(filter) &&
      getFilterFacetValues(filter).length > 0
  );

  return {
    selectedFilters,
    unselectedFilters,
    selectFilter,
    unselectFilter,
    isLoadingFilters: isLoadingOptions,
    someSelectFilterApplied,
  };
};
